import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import authService from '../../api/authService';
import { navigateToRoleDashboard } from '../../utils/navigation';

const SupportPortalLanding = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(email, password);
      
      if (response.token) {
        const user = authService.getCurrentUser();
        if (user) {
          const userJson = JSON.stringify(user);
          // Store in both localStorage and sessionStorage
          // localStorage.setItem('authUser', userJson);
          sessionStorage.setItem('authUser', userJson);
          if (rememberMe) {
            localStorage.setItem('rememberMe', 'true');
            localStorage.setItem('rememberedEmail', email);
          }
          navigateToRoleDashboard(user.role, navigate);
          toast.success('Login successful!');
        } else {
          throw new Error('Failed to decode user information');
        }
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Blurred Background Image */}
      <div 
        className="fixed inset-0 w-full h-full"
        style={{
          backgroundImage: 'url(/landing_page_image.avif)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(8px)',
          transform: 'scale(1.1)'
        }}
      />
      
      {/* Overlay for better text readability */}
      <div 
        className="fixed inset-0 w-full h-full"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)'
        }}
      />

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col">
        
        {/* Header with Title */}
        <header className="w-full py-8 text-center">
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white"
            style={{
              textShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3)',
              letterSpacing: '-0.02em'
            }}
          >
            Helpdesk Support Portal
          </h1>
        </header>

        {/* Main Content - Login Form */}
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            {/* Login Card */}
            <div 
              className="rounded-2xl p-8 md:p-10 shadow-2xl"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <h2 
                className="text-3xl font-bold text-center mb-8"
                style={{ color: '#1a1a1a' }}
              >
                Login
              </h2>

              <form onSubmit={handleLoginSubmit} className="space-y-6">
                {error && (
                  <div 
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(254, 202, 202, 0.9)',
                      border: '1px solid rgba(239, 68, 68, 0.3)'
                    }}
                  >
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                )}

                {/* Email Input */}
                <div>
                  <label 
                    htmlFor="email" 
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#333333' }}
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 transition-all focus:outline-none"
                    style={{
                      borderColor: '#D1D5DB',
                      backgroundColor: '#FFFFFF',
                      color: '#1a1a1a',
                      fontSize: '16px'
                    }}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3B82F6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Password Input */}
                <div>
                  <label 
                    htmlFor="password" 
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#333333' }}
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 transition-all focus:outline-none"
                    style={{
                      borderColor: '#D1D5DB',
                      backgroundColor: '#FFFFFF',
                      color: '#1a1a1a',
                      fontSize: '16px'
                    }}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3B82F6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Remember Me Checkbox */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="remember"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                      style={{ accentColor: '#3B82F6' }}
                    />
                    <label 
                      htmlFor="remember" 
                      className="ml-2 text-sm font-medium"
                      style={{ color: '#4a4a4a' }}
                    >
                      Remember me
                    </label>
                  </div>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full py-3.5 rounded-lg font-bold text-white text-base transition-all"
                  style={{
                    backgroundColor: loading || !email || !password ? '#9CA3AF' : '#3B82F6',
                    cursor: loading || !email || !password ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                    fontSize: '16px'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && email && password) {
                      e.currentTarget.style.backgroundColor = '#2563EB';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && email && password) {
                      e.currentTarget.style.backgroundColor = '#3B82F6';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(59, 130, 246, 0.4)';
                    }
                  }}
                >
                  {loading ? 'Signing In...' : 'Login'}
                </button>

                {/* Register Link */}
                <div className="text-center mt-6">
                  <p className="text-sm" style={{ color: '#6B7280' }}>
                    Don't have an account?{' '}
                    <Link 
                      to="/signup" 
                      className="font-semibold hover:underline transition-colors"
                      style={{ color: '#3B82F6' }}
                    >
                      Register here
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="w-full py-6 text-center">
          <p 
            className="text-sm font-medium text-white"
            style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
            }}
          >
            Support & Services
          </p>
        </footer>

      </div>
    </div>
  );
};

export default SupportPortalLanding;
